import { useEffect, useState } from 'react'
import { placeOrder } from '../api'
import { price as fmtPrice } from '../format'
import type { Depth, Side } from '../types'

/**
 * `Yeni Teklif` — gölge emir girişi. `Emirlerim` sekmesinin başında durur.
 *
 * Kapalı kontratta hiç render edilmez: o kontrat bir daha işlem görmeyeceği
 * için girilen emir sonsuza kadar açık kalırdı (sunucu da 409 ile reddediyor).
 *
 * Emir açıldıktan sonra liste elde güncellenmiyor: sunucu emir listesi
 * değişince bir sonraki turda WebSocket'ten tamamını yolluyor. Tek kaynak soket
 * olarak kalıyor, iki güncelleme yolu birbiriyle yarışmıyor.
 */

/** Fiyat adımı — sunucudaki `PRICE_TICK` ile aynı olmalı. */
const TIK = 0.1

interface Props {
  contract: string
  depth: Depth | null
  /**
   * Dışarıdan gelen fiyat önerisi — mobilde emir defterine dokunmak fiyatı
   * buraya yazar (`TradePage`). `sira` her dokunuşta artıyor: aynı fiyata
   * ikinci kez dokunulduğunda değer değişmediği için efekt tetiklenmezdi.
   */
  oneri?: { fiyat: number; sira: number } | null
}

export function NewOrderForm({ contract, depth, oneri = null }: Props) {
  const [side, setSide] = useState<Side>('BUY')
  const [price, setPrice] = useState('')
  const [quantity, setQuantity] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [gonderiliyor, setGonderiliyor] = useState(false)
  const [elleGirildi, setElleGirildi] = useState(false)

  // Fiyat kutusu seçilen tarafın en iyi seviyesini izler — boş kutuya sıfırdan
  // yazmak yerine oradan oynamak daha hızlı.
  //
  // Ama kullanıcı kutuya DOKUNDUĞU anda izleme durur. Aksi hâlde en iyi fiyat
  // saniyede bir değiştiği için yazılan değer bir sonraki tikte siliniyordu:
  // 4140 yazıp emir açınca emir 4111,5'ten açılıyordu (canlı testte yakalandı).
  const enIyi = side === 'BUY' ? depth?.bids?.[0]?.price : depth?.asks?.[0]?.price
  useEffect(() => {
    if (elleGirildi) return
    setPrice(enIyi != null ? String(enIyi) : '')
  }, [enIyi, elleGirildi])

  // Kontrat ya da taraf değişince kutu yeniden piyasayı izlemeye başlar:
  // önceki kontrat için yazılmış fiyatın yenisinde durması yanlış olurdu.
  useEffect(() => {
    setElleGirildi(false)
    setHata(null)
  }, [contract, side])

  // Defterden gelen fiyat. Elle yazmakla aynı sonuç: izleme durur, değer
  // kullanıcınındır. Bu efekt yukarıdaki izleme efektinden SONRA tanımlı —
  // ikisi aynı turda çalışırsa son sözü bu söylesin.
  const oneriSira = oneri?.sira
  const oneriFiyat = oneri?.fiyat
  useEffect(() => {
    if (oneriFiyat == null) return
    setElleGirildi(true)
    setPrice(String(oneriFiyat))
    // Bilerek yalnız `sira`ya bağlı: tetikleyici olan şey dokunuşun kendisi,
    // fiyatın değişmiş olması değil.

  }, [oneriSira])

  async function gonder(event: React.FormEvent) {
    event.preventDefault()
    setHata(null)
    setGonderiliyor(true)
    try {
      await placeOrder({ contract, side, price: Number(price), quantity: Number(quantity) })
      setQuantity('')
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Emir açılamadı')
    } finally {
      setGonderiliyor(false)
    }
  }

  // Çapraz fiyat ENGELLENMİYOR, kırpılacağı önceden gösteriliyor: sunucudaki
  // politika fiyatı bir tik pasife çekip emri yine açıyor. Buradaki hesap
  // yalnız önizleme — asıl kırpma sunucuda, çünkü tahta bir tur eskiyebilir
  // ve son söz o anki gerçek deftere ait.
  const enIyiAlis = depth?.bids?.[0]?.price ?? null
  const enIyiSatis = depth?.asks?.[0]?.price ?? null
  const p = Number(price)
  const izgara = (v: number) => Math.round(v / TIK) * TIK
  const capraz =
    !p || Number.isNaN(p)
      ? null
      : side === 'BUY' && enIyiSatis != null && p >= enIyiSatis
        ? { karsi: enIyiSatis, yeni: izgara(enIyiSatis - TIK) }
        : side === 'SELL' && enIyiAlis != null && p <= enIyiAlis
          ? { karsi: enIyiAlis, yeni: izgara(enIyiAlis + TIK) }
          : null

  const gecerli = Number(price) > 0 && Number(quantity) > 0

  return (
    <>
      <div className="shadow-section shadow-form-title">Yeni Teklif</div>
      <form className="order-form" onSubmit={gonder}>
        <div className="side-toggle" role="group" aria-label="Emir yönü">
          {(['BUY', 'SELL'] as Side[]).map((s) => (
            <button
              key={s}
              type="button"
              className={`side-btn ${s === 'BUY' ? 'buy' : 'sell'}${side === s ? ' active' : ''}`}
              aria-pressed={side === s}
              onClick={() => setSide(s)}
            >
              {s === 'BUY' ? 'Al' : 'Sat'}
            </button>
          ))}
        </div>

        <label className="order-field">
          <span>Fiyat</span>
          {/* `inputMode` telefonda hangi klavyenin açılacağını söyler: fiyatta
              ondalıklı tuş takımı, miktarda salt rakam. `type="number"` yerinde
              kalıyor — doğrulama, adım (step) ve masaüstündeki ok tuşları ondan
              geliyor. */}
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            min="0"
            value={price}
            onChange={(e) => {
              setElleGirildi(true)      // artık piyasayı izleme, kullanıcının değeri
              setPrice(e.target.value)
            }}
            required
          />
        </label>

        {/* Miktar birimi LOT — EPİAŞ tahtasındaki `q` alanı lot sayısı taşıyor.
            Birim etikette duruyor (yer tutucuda değil): yazmaya başlayınca
            kaybolmasın. */}
        <label className="order-field">
          <span>Miktar (Lot)</span>
          <input
            type="number"
            inputMode="numeric"
            step="1"
            min="0"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            required
          />
        </label>

        <button type="submit" className="order-submit" disabled={!gecerli || gonderiliyor}>
          {gonderiliyor ? '…' : 'Emir Aç'}
        </button>
      </form>

      {/* Uyarı değil bilgilendirme: emir yine açılacak, yalnız fiyatı
          değişecek. Yeni fiyat açıkça yazılıyor ki sürpriz olmasın. */}
      {capraz && (
        <div className="order-note">
          Bu fiyat {fmtPrice(capraz.karsi)} karşı tarafını vurur — emir{' '}
          <b>{fmtPrice(capraz.yeni)}</b> fiyatına kırpılarak açılacak. (Süpürme
          henüz simüle edilmiyor; kayıtlara kırpma olarak geçer.)
        </div>
      )}
      {hata && <div className="order-error" role="alert">{hata}</div>}
    </>
  )
}
